import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff, User, Lock, AlertCircle, Info } from "lucide-react";
import { useTranslation } from "react-i18next";
import { LogoDark } from "../components/Logo";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function Login() {
  const { t } = useTranslation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(username.trim(), password);
      toast.success(t("login.success"));
      setTimeout(() => {
        navigate(u.role === "teacher" ? "/teacher/dashboard" : "/student/dashboard", { replace: true });
      }, 400);
    } catch (err) {
      setError(t("login.invalid"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-violet-50 via-slate-50 to-indigo-50">
      {/* Language switcher in top-right */}
      <div className="absolute right-4 top-4 z-10">
        <LanguageSwitcher variant="light" />
      </div>

      {/* Decorative blobs */}
      <div className="pointer-events-none absolute -top-32 -left-32 h-96 w-96 rounded-full bg-violet-200/40 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-indigo-200/40 blur-3xl" />
      <div className="pointer-events-none absolute right-1/4 top-1/3 h-24 w-24 rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 opacity-20 blur-2xl" />

      <div className="relative flex min-h-screen items-center justify-center px-4 py-12">
        <div className="w-full max-w-md animate-fade-up">
          <div className="rounded-3xl bg-white/90 p-8 shadow-xl shadow-slate-900/5 backdrop-blur-sm sm:p-10" data-testid="login-card">
            <div className="flex justify-center pb-6">
              <LogoDark size={48} />
            </div>

            <div className="space-y-1 pb-6 text-center">
              <p className="text-base font-semibold italic text-slate-700">{t("login.tagline1")}</p>
              <p className="text-base font-semibold italic text-slate-700">{t("login.tagline2")}</p>
              <p className="text-base font-semibold italic text-slate-700">{t("login.tagline3")}</p>
              <p className="text-base font-semibold italic text-slate-700">{t("login.tagline4")}</p>
            </div>

            <div className="my-6 h-px w-full bg-slate-200" />

            <h1 className="text-center text-xl font-bold text-slate-900">{t("login.welcome")}</h1>
            <p className="text-center text-sm text-slate-500">{t("login.subtitle")}</p>

            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <div className="relative">
                <User size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  data-testid="login-username"
                  type="text"
                  placeholder={t("login.username")}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  autoComplete="username"
                  className="w-full rounded-xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                />
              </div>

              <div className="relative">
                <Lock size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  data-testid="login-password"
                  type={showPw ? "text" : "password"}
                  placeholder={t("login.password")}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className={`w-full rounded-xl border bg-white py-3 pl-11 pr-12 text-slate-900 outline-none transition focus:ring-2 ${
                    error ? "border-rose-300 focus:border-rose-500 focus:ring-rose-100" : "border-slate-200 focus:border-indigo-500 focus:ring-indigo-100"
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  data-testid="login-toggle-password"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  aria-label="Toggle password visibility"
                >
                  {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              {error && (
                <div data-testid="login-error" className="flex items-center gap-2 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
                  <AlertCircle size={16} />
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid="login-submit"
                className="w-full rounded-xl bg-indigo-600 py-3 text-base font-semibold text-white shadow-lg shadow-indigo-600/20 transition hover:bg-indigo-700 active:scale-[0.99] disabled:opacity-60"
              >
                {loading ? t("login.signing_in") : t("login.login_cta")}
              </button>

              <div className="text-center">
                <Link
                  to="/forgot-password"
                  data-testid="login-forgot-link"
                  className="text-sm font-semibold text-indigo-600 hover:text-indigo-700 hover:underline"
                >
                  {t("login.forgot")}
                </Link>
              </div>
            </form>

            <div className="my-5 flex items-center gap-3">
              <div className="h-px flex-1 bg-slate-200" />
              <span className="text-xs font-medium uppercase tracking-wide text-slate-400">{t("login.or")}</span>
              <div className="h-px flex-1 bg-slate-200" />
            </div>

            <button
              type="button"
              data-testid="login-google-btn"
              onClick={() => {
                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                const redirectUrl = window.location.origin + "/auth/callback";
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              className="flex w-full items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 active:scale-[0.99]"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
              </svg>
              {t("login.google_cta")}
            </button>
            <p className="mt-2 text-center text-xs text-slate-500">{t("login.google_hint")}</p>
          </div>

          {/* AI translation disclaimer */}
          <div
            className="mt-4 flex items-start gap-2 rounded-2xl border border-amber-200 bg-amber-50/80 p-3.5 text-xs text-amber-900 shadow-sm backdrop-blur-sm"
            data-testid="ai-translation-disclaimer"
          >
            <Info size={14} className="mt-0.5 flex-none text-amber-600" />
            <p className="leading-relaxed">{t("login.ai_disclaimer")}</p>
          </div>

        </div>
      </div>
    </div>
  );
}
